import React from 'react';
import { Box, Tooltip } from '@mui/material';

// One status chip for the whole app (docs/ui-refresh-mockup.html spec):
// pill, colored dot + label, semantic color only — never the teal accent.
// Tones: neutral | info | good | warn | crit.

const TONE_BY_STATUS = {
  // neutral — not yet in motion
  draft: 'neutral',
  pending: 'neutral',
  inactive: 'neutral',
  voided: 'neutral',
  none: 'neutral',
  // info — in flight
  sent: 'info',
  invoiced: 'info',
  submitted: 'info',
  processing: 'info',
  processed: 'info',
  in_review: 'info',
  scheduled: 'info',
  // good — done / positive
  paid: 'good',
  invoice_paid: 'good',
  approved: 'good',
  active: 'good',
  completed: 'good',
  reimbursed: 'good',
  open: 'good',
  // neutral — normal in-flight states that shouldn't shout
  unpaid: 'neutral',
  // warn — needs action
  payroll_ready: 'warn',
  partial: 'warn',
  on_hold: 'warn',
  closed: 'warn',
  locked: 'warn',
  // crit — wrong / late
  overdue: 'crit',
  rejected: 'crit',
  denied: 'crit',
  cancelled: 'crit',
  canceled: 'crit',
  failed: 'crit',
  error: 'crit',
};

// Shared status → tone lookup (neutral|info|good|warn|crit) so screens that
// color by status (e.g. PaymentPipeline stage cards) match the chips.
export const getStatusTone = (status) => {
  const key = String(status || '').toLowerCase();
  // Own keys only, so 'constructor' / 'toString' never resolve to Object.prototype members.
  return Object.prototype.hasOwnProperty.call(TONE_BY_STATUS, key) ? TONE_BY_STATUS[key] : 'neutral';
};

// Tone → theme palette key (neutral has no palette family; callers use text.secondary).
export const TONE_PALETTE_KEY = { info: 'info', good: 'success', warn: 'warning', crit: 'error' };

const prettify = (status) =>
  String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * StatusChip — unified status pill.
 *
 * @param {string} status    Status key (e.g. 'draft', 'sent', 'paid', 'overdue').
 * @param {string} [label]   Display text; defaults to a prettified status.
 * @param {string} [tone]    Override the built-in tone mapping (neutral|info|good|warn|crit).
 * @param {string} [title]   Optional tooltip explaining the status.
 * @param {func}   [onClick] Makes the chip interactive (pointer cursor + hover).
 */
export default function StatusChip({ status, label, tone, title, onClick, sx = {} }) {
  const resolvedTone = tone || getStatusTone(status);

  const toneSx = (theme) => {
    const p = theme.palette;
    switch (resolvedTone) {
      case 'info':
        return { color: p.info.main, bgcolor: p.info.soft };
      case 'good':
        return { color: p.success.main, bgcolor: p.success.soft };
      case 'warn':
        return { color: p.warning.main, bgcolor: p.warning.soft };
      case 'crit':
        return { color: p.error.main, bgcolor: p.error.soft };
      default:
        return {
          color: p.text.secondary,
          bgcolor: p.background.surface2,
          border: `1px solid ${p.divider}`,
        };
    }
  };

  const chip = (
    <Box
      component="span"
      onClick={onClick}
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1.5,
        padding: '3px 9px',
        borderRadius: '999px',
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        '&::before': {
          content: '""',
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: 'currentColor',
          flex: 'none',
        },
        ...(onClick && { cursor: 'pointer', '&:hover': { opacity: 0.75 } }),
        ...toneSx(theme),
        ...(typeof sx === 'function' ? sx(theme) : sx),
      })}
    >
      {label || prettify(status)}
    </Box>
  );

  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
}
